import { useEffect, useRef } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { AlertTriangle, X } from 'lucide-react';
import './AnomalyToast.css';

const MAX_VISIBLE = 5;
const AUTO_DISMISS_MS = 30_000;

export function AnomalyToast() {
  const { unacknowledgedAlerts, dismissAlert } = useSessionStore();
  const visibleAlerts = unacknowledgedAlerts.slice(0, MAX_VISIBLE);
  const older = unacknowledgedAlerts.length - MAX_VISIBLE;
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Auto-dismiss warnings after 30s
  useEffect(() => {
    for (const alert of unacknowledgedAlerts) {
      if (alert.severity === 'warning' && !timersRef.current.has(alert.anomaly_id)) {
        const timer = setTimeout(() => {
          dismissAlert(alert.anomaly_id);
          timersRef.current.delete(alert.anomaly_id);
        }, AUTO_DISMISS_MS);
        timersRef.current.set(alert.anomaly_id, timer);
      }
    }
  }, [unacknowledgedAlerts, dismissAlert]);

  if (visibleAlerts.length === 0) return null;

  return (
    <div className="anomaly-toast-stack">
      {visibleAlerts.map((alert) => (
        <div
          key={alert.anomaly_id}
          className={`anomaly-toast ${alert.severity === 'critical' ? 'anomaly-toast--critical' : 'anomaly-toast--warning'}`}
        >
          <AlertTriangle size={14} className="anomaly-toast-icon" />
          <div className="anomaly-toast-content">
            <p className="anomaly-toast-title">
              {alert.anomaly_type === 'loop' && 'Loop Detected'}
              {alert.anomaly_type === 'context_overflow' && 'Context Overflow'}
              {alert.anomaly_type === 'error_cascade' && 'Error Cascade'}
              {alert.anomaly_type === 'stall' && 'Agent Stall'}
            </p>
            <p className="anomaly-toast-desc">{alert.description}</p>
            <p className="anomaly-toast-agents">
              {alert.involved_agents.join(' → ')}
            </p>
          </div>
          <button
            className="anomaly-toast-dismiss"
            onClick={() => dismissAlert(alert.anomaly_id)}
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      ))}
      {older > 0 && (
        <div className="anomaly-toast-older">{older} older alert{older !== 1 ? 's' : ''}</div>
      )}
    </div>
  );
}
